import { useEffect } from 'react';

const VAR_NAME = '--app-vvh';

let consumerCount = 0;
let teardown: (() => void) | null = null;

/**
 * Mirrors `window.visualViewport.height` into a CSS custom property
 * (`--app-vvh`) on `<html>`, written via direct DOM mutation.
 *
 * iOS Safari/Chrome do NOT shrink the layout viewport (or `dvh`) when the
 * software keyboard opens — they only resize the *visual* viewport. So `dvh`
 * cannot be used to clamp a modal above the keyboard. This hook bridges that
 * gap without React state, so the modal can re-size via plain CSS without
 * triggering rerenders that reset HeroUI Autocomplete internal state.
 *
 * Usage in CSS / Tailwind: `max-h-[calc(var(--app-vvh,100dvh)-1rem)]`.
 */
export const useVisualViewportVar = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    consumerCount += 1;

    if (consumerCount === 1) {
      const update = () => {
        const h = window.visualViewport?.height ?? window.innerHeight;
        document.documentElement.style.setProperty(VAR_NAME, `${h}px`);
      };
      update();
      window.visualViewport?.addEventListener('resize', update);
      window.visualViewport?.addEventListener('scroll', update);
      window.addEventListener('resize', update);
      teardown = () => {
        window.visualViewport?.removeEventListener('resize', update);
        window.visualViewport?.removeEventListener('scroll', update);
        window.removeEventListener('resize', update);
      };
    }

    return () => {
      consumerCount -= 1;
      if (consumerCount === 0) {
        teardown?.();
        teardown = null;
        document.documentElement.style.removeProperty(VAR_NAME);
      }
    };
  }, []);
};
